import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Loader2,
  PackageCheck,
  Phone,
  Search,
  Truck,
} from "lucide-react";
import { FaWhatsapp as WhatsApp } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Footer, Header } from "./Home";
import ArticleSeo from "./articles/ArticleSeo";
import { formatNaira, SITE_URL, WHATSAPP_NUMBER } from "./homeTestsData";
import { GuestAccountMenu } from "./HomeTests";

const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3000"
).replace(/\/$/, "");

const CLIENT_ID_KEY = "zaviramedHomeTestClientId";
const CLIENT_NAME_KEY = "zaviramedHomeTestClientName";
const CLIENT_TOKEN_KEY = "zaviramedHomeTestClientToken";

const statusStyles = {
  pending: "bg-amber-50 text-amber-700 border-amber-100",
  paid_product: "bg-emerald-50 text-[#075E54] border-emerald-100",
  failed: "bg-red-50 text-red-700 border-red-100",
  processing: "bg-slate-100 text-slate-700 border-slate-200",
  dispatch: "bg-slate-100 text-slate-700 border-slate-200",
  delivered: "bg-emerald-50 text-[#075E54] border-emerald-100",
};

function saveAccount(client) {
  if (client?._id) localStorage.setItem(CLIENT_ID_KEY, client._id);
  if (client?.name) localStorage.setItem(CLIENT_NAME_KEY, client.name);
  window.dispatchEvent(new Event("zaviramed-home-test-account"));
}

function getClientToken() {
  if (typeof window === "undefined") return "";

  return localStorage.getItem(CLIENT_TOKEN_KEY) || "";
}

function StatusBadge({ status, label }) {
  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold ${
        statusStyles[status] || "border-slate-200 bg-slate-50 text-slate-600"
      }`}
    >
      {label || String(status || "Pending").replace(/_/g, " ")}
    </span>
  );
}

function OrderCard({ order }) {
  const trackingKey = order.trackingKey || order.orderId;
  const assistantMessage = `Hello Zaviramed, I need help with my home test order.`;

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold tracking-[-0.03em] text-[#075E54]">
              {order.title || "Home test order"}
            </h2>
            <StatusBadge status={order.status} label={order.statusLabel} />
          </div>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            {order.description || "Your Zaviramed home testing order."}
          </p>
          <div className="mt-4 grid gap-2 text-xs font-semibold text-slate-500 sm:grid-cols-3">
            <span>Total: {formatNaira(order.amountPaid || order.price)}</span>
            <span>Delivery included</span>
            <span>
              Ordered:{" "}
              {order.requestedAt
                ? new Date(order.requestedAt).toLocaleDateString()
                : "Today"}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Link
          to={`/orders/${trackingKey}`}
          className="inline-flex items-center gap-2 rounded-xl bg-[#075E54] px-4 py-2.5 text-xs font-black text-white transition hover:bg-[#064f47] active:scale-[0.98]"
        >
          <Truck size={15} />
          Track
        </Link>
        <a
          href="tel:+2348069804062"
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-black text-slate-700 transition hover:bg-slate-50 active:scale-[0.98]"
        >
          <Phone size={15} />
          Call assistant
        </a>
        <a
          href={
            order.whatsappUrl ||
            `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
              assistantMessage
            )}`
          }
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2.5 text-xs font-black text-[#075E54] transition hover:bg-emerald-100 active:scale-[0.98]"
        >
          <WhatsApp size={15} />
          WhatsApp
        </a>
      </div>
    </article>
  );
}

export default function HomeTestOrders() {
  const [orders, setOrders] = useState([]);
  const [client, setClient] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientToken, setClientToken] = useState(() => getClientToken());

  useEffect(() => {
    const syncToken = () => {
      setClientToken(getClientToken());
    };

    window.addEventListener("storage", syncToken);
    window.addEventListener("zaviramed-home-test-account", syncToken);

    return () => {
      window.removeEventListener("storage", syncToken);
      window.removeEventListener("zaviramed-home-test-account", syncToken);
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const fetchOrders = async () => {
      if (!clientToken) {
        setError("");
        setOrders([]);
        setClient(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await axios.get(`${BACKEND_URL}/home-tests/orders`, {
          headers: {
            Authorization: `Bearer ${clientToken}`,
          },
        });

        if (!alive) return;

        const nextClient = response.data?.client || null;
        setClient(nextClient);
        setOrders(response.data?.orders || []);
        saveAccount(nextClient);
      } catch (err) {
        if (!alive) return;
        if (err.response?.status === 401) {
          localStorage.removeItem(CLIENT_TOKEN_KEY);
          setClientToken("");
          window.dispatchEvent(new Event("zaviramed-home-test-account"));
        }
        setError(err.response?.data?.message || "Could not load your orders.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchOrders();

    return () => {
      alive = false;
    };
  }, [clientToken]);

  const filteredOrders = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return orders;

    return orders.filter((order) =>
      [order.title, order.status, order.statusLabel]
        .join(" ")
        .toLowerCase()
        .includes(value)
    );
  }, [orders, search]);

  return (
    <main className="min-h-screen bg-[#f8faf9] text-slate-950">
      <ArticleSeo
        title="Your Home Test Orders | Zaviramed"
        description="Track Zaviramed home test orders, status updates and rider details."
        canonical={`${SITE_URL}/orders`}
        type="website"
      />

      <Header />
      <GuestAccountMenu />

      <div className="mx-auto w-full max-w-6xl px-4 pb-20 pt-28 sm:px-6 lg:px-8">
        <section className="rounded-xl border border-slate-200 bg-white p-5  sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              {/* <p className="text-xs font-black uppercase tracking-[0.18em] text-[#25D366]">
                Home tests
              </p> */}
              <h1 className=" text-2xl font-semibold tracking-[-0.04em] text-[#075E54] sm:text-3xl">
                Orders
              </h1>
              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
                View your kits, payment status, dispatch updates and rider
                details in one place. Delivery is included in the price.
              </p>
            </div>

            <label className="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 sm:max-w-xs">
              <Search size={16} className="text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400"
                placeholder="Search orders"
              />
            </label>
          </div>
        </section>

        <section className="mt-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white py-16 text-sm font-bold text-slate-500 shadow-sm">
              <Loader2 className="mr-2 animate-spin" size={18} />
              Loading orders
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-100 bg-red-50 p-5 text-sm font-semibold text-red-700">
              {error}
            </div>
          ) : !clientToken ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
              <PackageCheck className="mx-auto text-[#075E54]" size={34} />
              <h2 className="mt-3 text-lg font-black text-slate-950">
                Login to view your orders
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
                For privacy, orders only show after you sign in with the email
                or WhatsApp number on your Zaviramed account.
              </p>
              <Link
                to="/home-test"
                className="mt-5 inline-flex rounded-xl bg-[#075E54] px-5 py-3 text-sm font-black text-white"
              >
                Shop home tests
              </Link>
            </div>
          ) : filteredOrders.length ? (
            filteredOrders.map((order) => (
              <OrderCard key={order.reference || order.orderId} order={order} />
            ))
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
              <h2 className="text-lg font-black text-slate-950">
                No orders yet
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                {client?.name ? `${client.name}, ` : ""}your home test orders
                will show here after checkout starts.
              </p>
            </div>
          )}
        </section>
      </div>

      <Footer />
    </main>
  );
}
